import { useRef, useState, useEffect } from 'react';

export interface UseGamePlayerOptions {
  gameSlug?: string;
}

export interface GamePlayerState {
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  gamePlayerRef: React.RefObject<HTMLDivElement | null>;
  showBackToGame: boolean;
  gameStarted: boolean;
  isLoading: boolean;
  isTheaterMode: boolean;
  handlePlayClick: () => void;
  handleIframeLoad: () => void;
  scrollToGame: () => void;
  toggleTheaterMode: () => void;
  showCoverOverlay?: boolean;
  hasInitializedFullscreen?: boolean;
  handleOverlayPlay?: () => void;
  showBlackScreen?: boolean;
}

export function useGamePlayer(options: UseGamePlayerOptions = {}): GamePlayerState {
  const { gameSlug } = options;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const gamePlayerRef = useRef<HTMLDivElement | null>(null);
  const [showBackToGame, setShowBackToGame] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [showCoverOverlay, setShowCoverOverlay] = useState(false);
  const [hasInitializedFullscreen, setHasInitializedFullscreen] = useState(false);
  const [showBlackScreen, setShowBlackScreen] = useState(false);

  const isBadParentingHome = gameSlug === 'bad-parenting';

  const handlePlayClick = () => {
    setIsLoading(true);
    setGameStarted(true);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    if (isBadParentingHome && !hasInitializedFullscreen) {
      setShowCoverOverlay(true);
    }
  };

  const handleOverlayPlay = async () => {
    const container = gamePlayerRef.current;
    if (!container) return;

    if (!gameStarted) {
      setIsLoading(true);
      setGameStarted(true);
    }

    setShowCoverOverlay(false);
    setShowBlackScreen(true);

    const el = container as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
      msRequestFullscreen?: () => Promise<void> | void;
    };

    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen();
      } else if (el.msRequestFullscreen) {
        await el.msRequestFullscreen();
      }
      setTimeout(() => {
        setShowBlackScreen(false);
        setHasInitializedFullscreen(true);
      }, 1500);
    } catch (err) {
      console.error('Fullscreen request failed:', err);
      setTimeout(() => {
        setShowBlackScreen(false);
        setHasInitializedFullscreen(true);
      }, 1500);
    }
  };

  const scrollToGame = () => {
    if (gamePlayerRef.current) {
      gamePlayerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleTheaterMode = () => {
    setIsTheaterMode(prev => !prev);
  };
  useEffect(() => {
    if (isTheaterMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isTheaterMode]);
  useEffect(() => {
    const onFsChange = () => {
      const isFs = !!(document.fullscreenElement || (document as any).webkitFullscreenElement || (document as any).msFullscreenElement);
      if (!isFs && hasInitializedFullscreen && !showCoverOverlay) {
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('msfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('msfullscreenchange', onFsChange);
    };
  }, [hasInitializedFullscreen, showCoverOverlay]);
  useEffect(() => {
    if (gameSlug !== undefined) {
      setGameStarted(false);
      setIsLoading(false);
      setShowCoverOverlay(false);
      setHasInitializedFullscreen(false);
      setShowBlackScreen(false);
    }
  }, [gameSlug]);
  useEffect(() => {
    const handleScroll = () => {
      if (gamePlayerRef.current) {
        const rect = gamePlayerRef.current.getBoundingClientRect();
        setShowBackToGame(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return {
    iframeRef,
    gamePlayerRef,
    showBackToGame,
    gameStarted,
    isLoading,
    isTheaterMode,
    handlePlayClick,
    handleIframeLoad,
    scrollToGame,
    toggleTheaterMode,
    showCoverOverlay,
    hasInitializedFullscreen,
    handleOverlayPlay,
    showBlackScreen,
  };
}
